import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFDOB from '../components/RMFDOB/RMFDOB';
import RMFDepositLimit from '../components/RMFDepositLimit/RMFDepositLimit';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFStandaloneFinalPage from '../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFSumSub from '../components/RMFSumSub/RMFSumSub';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfBRFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const { forkSignupDepositCheckDone } = useRMFBaseSteps();

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [forkSignupDepositCheckDone]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );

  const invokeLegalNameSave = new Invoke(Service.RmfLegalNameSave, RMFAddress, RMFLegalName);

  const invokeEnableRealMoneyFlag = new Invoke(
    Service.RmfEnableRealMoneyFlag,
    forkRmfExit,
    RMFDepositLimit
  );

  const invokeAddressSave = new Invoke(Service.RmfAddressSave, RMFDepositLimit, RMFAddress);
  const invokeRmfDobUpdate = new Invoke(Service.RmfDobUpdate, RMFLegalName, RMFDOB);
  const forkDobCheck = new Fork(
    Guard.RmfDobEnabled,
    [RMFDOB, invokeRmfDobUpdate, RMFLegalName],
    [RMFLegalName]
  );
  const invokeDOBCheck = new Invoke(Service.SignupDobFetch, forkDobCheck);
  const forkSumSubVerification = new Fork(
    Guard.CheckSumSubVerification,
    RMFDepositLimit,
    invokeDOBCheck
  );

  const forkSumSub = new Fork(Guard.ShowSumSubFirst, [RMFSumSub], invokeDOBCheck);
  const invokeDepositLimitSave = new Invoke(Service.RmfDepositLimitSave, invokeEnableRealMoneyFlag);

  const forkDepositLimits = new Fork(Guard.LimitsSelected, invokeEnableRealMoneyFlag, [
    invokeDepositLimitSave,
    invokeEnableRealMoneyFlag
  ]);

  return [
    forkSumSub,
    forkSumSubVerification,
    forkDobCheck,
    invokeLegalNameSave,
    RMFAddress,
    invokeAddressSave,
    RMFDepositLimit,
    forkDepositLimits,
    forkRmfExit,
    jumpToFeatureStep
  ];
};

export default useRmfBRFlow;
